import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as CouponIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import GoogleMapComponent from '../../components/common/GoogleMap';

const DEFAULT_LAT_LNG = { lat: 30.7333, lng: 76.5794 };
const OUTSIDE_DELIVERY_RADIUS_MESSAGE =
  'Delivery is not available for this location. It is outside our delivery area.';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem(appConfig.storage.token);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

const isVariantSellable = (variant) => {
  if (!variant) return false;
  if (variant.isActive === false || variant.visibleToCustomer === false) return false;
  return variant.isInStock === true;
};

const isLineSellable = (line) => {
  if (line?.isFreeSkuItem) return true;
  const hasVariants = Array.isArray(line.variants) && line.variants.length > 0;
  if (!hasVariants) return true;
  return isVariantSellable(line.selectedVariant);
};

const stripVariantForCart = (variant) => {
  if (!variant || typeof variant !== 'object') return variant;
  const { isInStock, ...rest } = variant;
  return rest;
};

const toStoredOption = (opt) => {
  if (!opt) return null;
  return {
    id: opt.id,
    name: opt.name,
    sku: opt.sku,
    additionalPrice: String(opt.additionalPrice ?? '0'),
    sortOrder: opt.sortOrder ?? 0,
    isDefault: Boolean(opt.isDefault)
  };
};

const resolveCustomizationsForCart = (productCustomizations, selected) => {
  const out = {};
  const defs = productCustomizations || {};
  Object.entries(selected || {}).forEach(([key, sel]) => {
    const def = defs[key];
    if (!def?.options?.length) return;
    if (Array.isArray(sel)) {
      const opts = sel
        .map((id) =>
          def.options.find(
            (o) =>
              String(o.id) === String(id) ||
              String(o.name) === String(id) ||
              String(o.sku).toUpperCase() === String(id).toUpperCase()
          )
        )
        .filter(Boolean)
        .map(toStoredOption);
      if (!opts.length) return;
      out[key] = opts.length === 1 ? opts[0] : opts;
    } else if (sel != null && sel !== '') {
      const opt = def.options.find(
        (o) =>
          String(o.id) === String(sel) ||
          String(o.name) === String(sel) ||
          String(o.sku).toUpperCase() === String(sel).toUpperCase()
      );
      if (opt) out[key] = toStoredOption(opt);
    }
  });
  return out;
};

const productImageUrl = (product) => {
  if (!product || typeof product !== 'object') return '';
  if (Array.isArray(product.images) && product.images[0]) return String(product.images[0]).trim();
  if (product.imageUrl) return String(product.imageUrl).trim();
  if (product.image) return String(product.image).trim();
  return '';
};

const cartSkusForCouponValidation = (lines) => {
  const out = [];
  (lines || []).forEach((line) => {
    if (line?.isFreeSkuItem) return;
    const sku = line?.selectedVariant?.sku != null ? String(line.selectedVariant.sku).trim() : '';
    if (sku) out.push(sku);
    const pid = line?.id != null ? String(line.id).trim() : '';
    if (pid) out.push(pid);
  });
  return [...new Set(out.map((s) => s.toUpperCase()))];
};

const normalizePodStore = (raw) => {
  if (!raw || raw.errorCode === 'ERR_NEW_ORDER') return null;
  const id = raw.storeId ?? raw.id;
  if (id == null || String(id).trim() === '') return null;
  const storeId = String(id);
  const podId =
    raw.podId != null && String(raw.podId).trim() !== '' ? String(raw.podId) : null;
  return { ...raw, id: storeId, storeId, podId };
};

const toCartProductLine = (line) => {
  const customizations = line.isFreeSkuItem
    ? {}
    : resolveCustomizationsForCart(line.customizations, line.selectedCustomizations);
  const variant = stripVariantForCart(line.selectedVariant);
  const unitPrice = line.isFreeSkuItem
    ? Number(variant?.price) || 0
    : (() => {
        let p = Number(variant?.price) || 0;
        Object.values(customizations).forEach((v) => {
          const arr = Array.isArray(v) ? v : [v];
          arr.forEach((opt) => {
            p += Number(opt?.additionalPrice) || 0;
          });
        });
        return p;
      })();

  return {
    productId: line.id,
    ...(line.urlId ? { urlId: String(line.urlId) } : {}),
    name: line.name,
    imageUrl: productImageUrl(line),
    quantity: line.quantity,
    price: unitPrice,
    variant,
    customizations,
    ...(line.isFreeSkuItem ? { isFreeSkuItem: true } : {})
  };
};

const ManualOrderCreation = () => {
  const navigate = useNavigate();
  const { actions } = useApp();

  const [page, setPage] = useState(1);

  // Customer
  const [customerSearch, setCustomerSearch] = useState('');
  const [customer, setCustomer] = useState(null);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerError, setCustomerError] = useState('');

  // Address + store
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [assignedStore, setAssignedStore] = useState(null);
  const [storeDistanceMeters, setStoreDistanceMeters] = useState(null);
  const [storeSelectionLoading, setStoreSelectionLoading] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    userName: '',
    houseDetails: '',
    streetDetails: '',
    landmark: '',
    latLong: { ...DEFAULT_LAT_LNG },
    pincode: ''
  });

  // Products
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductForAdd, setSelectedProductForAdd] = useState('');
  const [tempVariant, setTempVariant] = useState('');
  const [tempCustomizations, setTempCustomizations] = useState({});

  // Slots
  const [deliveryDay, setDeliveryDay] = useState('today');
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState('');
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);

  // Order
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponApplying, setCouponApplying] = useState(false);

  const [shippingCost, setShippingCost] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const [shippingDistanceKm, setShippingDistanceKm] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState('');

  const selectedAddress = useMemo(
    () => addresses.find((a) => a.addressId === selectedAddressId) || null,
    [addresses, selectedAddressId]
  );

  const selectedTimeSlot = useMemo(
    () => timeSlots.find((s) => s.id === selectedTimeSlotId) || null,
    [timeSlots, selectedTimeSlotId]
  );

  const calculateLinePrice = useCallback((line) => {
    let variantPrice = parseFloat(line.selectedVariant?.price || 0);
    const productData = products.find((p) => p.id === line.id);
    const customizationPrice = Object.entries(line.selectedCustomizations || {}).reduce((total, [key, value]) => {
      const customization = productData?.customizations?.[key];
      if (Array.isArray(value)) {
        return (
          total +
          value.reduce((subTotal, optionId) => {
            const option = customization?.options?.find((opt) => opt.id === optionId);
            return subTotal + parseFloat(option?.additionalPrice || 0);
          }, 0)
        );
      }
      const option = customization?.options?.find((opt) => opt.id === value);
      return total + parseFloat(option?.additionalPrice || 0);
    }, 0);
    return variantPrice + customizationPrice;
  }, [products]);

  const subtotal = useMemo(
    () =>
      selectedProducts.reduce(
        (total, line) => total + calculateLinePrice(line) * line.quantity,
        0
      ),
    [selectedProducts, calculateLinePrice]
  );

  const couponDiscount = useMemo(() => {
    if (!appliedCoupon?.couponCode) return 0;
    const v = Math.max(0, Number(appliedCoupon.couponValue) || 0);
    return Math.min(subtotal, v);
  }, [appliedCoupon, subtotal]);

  const ship = Math.max(0, Number(shippingCost) || 0);
  const orderTotal = Math.max(0, subtotal + ship - couponDiscount);

  const canContinueToSummary =
    Boolean(customer) &&
    Boolean(selectedAddress) &&
    Boolean(assignedStore?.id) &&
    selectedProducts.filter((l) => !l.isFreeSkuItem).length > 0 &&
    selectedProducts.every((line) => isLineSellable(line)) &&
    Boolean(selectedTimeSlotId) &&
    shippingCost != null &&
    !shippingError &&
    !shippingLoading;

  const resetFulfillmentDependentState = () => {
    setAssignedStore(null);
    setStoreDistanceMeters(null);
    setProducts([]);
    setSelectedProducts([]);
    setSelectedProductForAdd('');
    setTempVariant('');
    setTempCustomizations({});
    setTimeSlots([]);
    setSelectedTimeSlotId('');
    setAppliedCoupon(null);
    setCouponCode('');
    setShippingCost(null);
    setShippingError('');
    setShippingDistanceKm(null);
  };

  const fetchCustomerAddresses = async (userId) => {
    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/${userId}/addresses`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        const list = data.addresses || [];
        setAddresses(list);
        return list;
      }
      setAddresses([]);
      return [];
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]);
      return [];
    }
  };

  const fetchProductsForStore = async (storeId) => {
    if (!storeId) return;
    try {
      setProductsLoading(true);
      const response = await fetch(
        `${appConfig.api.baseURL}/products?status=active&limit=100&storeId=${storeId}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setProducts(data.data || []);
      } else {
        setProducts([]);
        actions.showSnackbar(data.message || 'Failed to load products', 'error');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      actions.showSnackbar('Error fetching products', 'error');
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchTimeSlots = async (storeId, day, podId) => {
    if (!storeId) return;
    try {
      setTimeSlotsLoading(true);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const deliveryDateStr = formatLocalDate(day === 'today' ? today : tomorrow);
      const qs = new URLSearchParams({
        storeId: String(storeId),
        deliveryDate: deliveryDateStr
      });
      if (podId) qs.set('podId', String(podId));

      const response = await fetch(
        `${appConfig.api.baseURL}/time-slots?${qs.toString()}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setTimeSlots((data.timeSlots || []).filter((slot) => slot.active));
      } else {
        setTimeSlots([]);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setTimeSlots([]);
      actions.showSnackbar('Error fetching time slots', 'error');
    } finally {
      setTimeSlotsLoading(false);
    }
  };

  const findNearestStore = async (lat, lng) => {
    try {
      setStoreSelectionLoading(true);
      const response = await fetch(`${appConfig.api.baseURL}/pods/getNearestStore`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          lat: Number(lat),
          lng: Number(lng)
        })
      });
      const data = await response.json();
      if (data?.errorCode === 'ERR_NEW_ORDER') {
        actions.showSnackbar(OUTSIDE_DELIVERY_RADIUS_MESSAGE, 'warning');
        return null;
      }
      const store = normalizePodStore(data);
      if (!store) {
        actions.showSnackbar(data.message || OUTSIDE_DELIVERY_RADIUS_MESSAGE, 'warning');
        return null;
      }
      return store;
    } catch (error) {
      console.error('Error finding nearest store:', error);
      actions.showSnackbar('Error finding nearest store', 'error');
      return null;
    } finally {
      setStoreSelectionLoading(false);
    }
  };

  const handleAddressSelection = async (addressId, addressOverride = null) => {
    setSelectedAddressId(addressId);
    resetFulfillmentDependentState();
    if (page === 2) setPage(1);

    if (!addressId) return;

    const address =
      addressOverride || addresses.find((a) => a.addressId === addressId);
    if (!address?.latLong?.lat || !address?.latLong?.lng) {
      actions.showSnackbar('Selected address is missing location coordinates', 'warning');
      return;
    }

    const store = await findNearestStore(address.latLong.lat, address.latLong.lng);
    if (!store?.id) return;

    setAssignedStore(store);
    setStoreDistanceMeters(
      typeof store.distance === 'number' ? store.distance : null
    );
    actions.showSnackbar(
      `Assigned store: ${store.storeName}${
        typeof store.distance === 'number'
          ? ` (${(store.distance / 1000).toFixed(2)} km)`
          : ''
      }`,
      'success'
    );

    await fetchProductsForStore(store.id);
    await fetchTimeSlots(store.id, deliveryDay, store.podId);
  };

  const searchCustomer = async () => {
    if (!customerSearch.trim() || customerSearch.length !== 10) {
      setCustomerError('Please enter a valid 10-digit mobile number');
      return;
    }

    setCustomerSearching(true);
    setCustomerError('');
    setCustomer(null);
    setAddresses([]);
    setSelectedAddressId('');
    resetFulfillmentDependentState();
    setPage(1);

    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/${customerSearch.trim()}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success && data.user) {
        setCustomer(data.user);
        await fetchCustomerAddresses(customerSearch.trim());
        setCustomerError('');
      } else {
        setCustomerError('Customer not found with this mobile number');
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      setCustomerError('Error searching customer. Please try again.');
    } finally {
      setCustomerSearching(false);
    }
  };

  const handleAddAddress = async () => {
    if (!customer) return;
    if (!newAddress.userName.trim() || !newAddress.houseDetails.trim() || !newAddress.pincode.trim()) {
      actions.showSnackbar('Please fill all required fields', 'error');
      return;
    }

    try {
      const response = await fetch(`${appConfig.api.baseURL}/users/${customer.userId}/addresses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newAddress,
          userId: customer.userId,
          timeStamp: Date.now()
        })
      });
      const data = await response.json();
      if (data.success) {
        await fetchCustomerAddresses(customer.userId);
        setAddAddressOpen(false);
        setNewAddress({
          userName: '',
          houseDetails: '',
          streetDetails: '',
          landmark: '',
          latLong: { ...DEFAULT_LAT_LNG },
          pincode: ''
        });
        actions.showSnackbar('Address added successfully', 'success');
        if (data.address?.addressId) {
          await handleAddressSelection(data.address.addressId, data.address);
        }
      } else {
        actions.showSnackbar(data.message || 'Error adding address', 'error');
      }
    } catch (error) {
      console.error('Error adding address:', error);
      actions.showSnackbar('Error adding address', 'error');
    }
  };

  useEffect(() => {
    if (assignedStore?.id) {
      setSelectedTimeSlotId('');
      fetchTimeSlots(assignedStore.id, deliveryDay, assignedStore.podId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDay]);

  useEffect(() => {
    const branchId = assignedStore?.branchId ?? assignedStore?.branch_id;
    const lat = Number(selectedAddress?.latLong?.lat);
    const lng = Number(selectedAddress?.latLong?.lng);
    if (!branchId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setShippingCost(null);
      setShippingError('');
      setShippingDistanceKm(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setShippingLoading(true);
      setShippingError('');
      try {
        const customerMobile = String(customer?.userId || customerSearch || '').replace(/\D/g, '').slice(-10);
        const response = await fetch(`${appConfig.api.baseURL}/delivery/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storeBranchId: String(branchId),
            customerLat: lat,
            customerLng: lng,
            cartTotal: subtotal,
            deliveryType: 'delivery',
            mobile: customerMobile,
            customerPhone: customerMobile
          })
        });
        const raw = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          setShippingCost(null);
          setShippingError(raw.message || raw.error || 'Failed to calculate delivery charge');
          return;
        }
        const charge =
          typeof raw.deliveryCharge === 'object' && raw.deliveryCharge != null
            ? Number(raw.deliveryCharge.charge)
            : Number(raw.deliveryCharge);
        setShippingCost(Number.isFinite(charge) ? charge : 0);
        setShippingDistanceKm(raw.distance ?? raw.deliveryCharge?.distance ?? null);
      } catch (error) {
        if (!cancelled) {
          setShippingCost(null);
          setShippingError('Failed to calculate delivery charge');
        }
      } finally {
        if (!cancelled) setShippingLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [assignedStore, selectedAddress, subtotal, customer, customerSearch]);

  const stripFreeSkuLines = (lines) => (lines || []).filter((l) => !l.isFreeSkuItem);

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      actions.showSnackbar('Enter a coupon code', 'error');
      return;
    }
    if (!customer?.userId || !assignedStore?.id) {
      actions.showSnackbar('Select customer and address first', 'error');
      return;
    }
    const paidLines = stripFreeSkuLines(selectedProducts);
    if (!paidLines.length) {
      actions.showSnackbar('Add products before applying a coupon', 'error');
      return;
    }

    setCouponApplying(true);
    try {
      const paidSubtotal = paidLines.reduce(
        (total, line) => total + calculateLinePrice(line) * line.quantity,
        0
      );
      const response = await fetch(`${appConfig.api.baseURL}/coupons/validate-coupon`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mobile: String(customer.userId).trim(),
          code,
          skus: cartSkusForCouponValidation(paidLines),
          cartTotal: paidSubtotal,
          storeId: String(assignedStore.id)
        })
      });
      const data = await response.json();
      if (!data.success) {
        actions.showSnackbar(data.message || 'Invalid coupon', 'error');
        return;
      }

      const couponRaw = data.coupon || {};
      const couponCodeApplied = String(
        data.couponCode || couponRaw.couponCode || code
      )
        .trim()
        .toUpperCase();
      const couponValue = Math.max(0, Number(data.couponValue ?? couponRaw.couponValue) || 0);
      const discountType = couponRaw.discount_type || couponRaw.discountType;

      if (discountType === 'free_sku') {
        const freeSkuAction = String(data.freeSkuAction || '').trim();
        const details = data.freeSkuDetails;
        let nextLines = stripFreeSkuLines(selectedProducts);
        if (freeSkuAction === 'auto_add' && details?.productId != null) {
          nextLines = [
            ...nextLines,
            {
              id: details.productId,
              name: details.productName || 'Free item',
              image: details.imageUrl || '',
              imageUrl: details.imageUrl || '',
              quantity: Math.max(1, Number(details.quantity) || 1),
              selectedVariant: details.variant || {},
              selectedCustomizations: {},
              customizations: {},
              isFreeSkuItem: true
            }
          ];
        }
        setSelectedProducts(nextLines);
        setAppliedCoupon({
          couponCode: couponCodeApplied,
          couponValue,
          discountType: 'free_sku',
          freeSkuAction
        });
      } else {
        setSelectedProducts(stripFreeSkuLines(selectedProducts));
        setAppliedCoupon({
          couponCode: couponCodeApplied,
          couponValue,
          discountType: discountType || 'discount_coupon'
        });
      }
      setCouponCode('');
      actions.showSnackbar('Coupon applied', 'success');
    } catch (error) {
      console.error('Error applying coupon:', error);
      actions.showSnackbar('Error applying coupon', 'error');
    } finally {
      setCouponApplying(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setSelectedProducts((prev) => stripFreeSkuLines(prev));
    actions.showSnackbar('Coupon removed', 'info');
  };

  const initCustomizations = (product) => {
    const initial = {};
    if (product?.customizations) {
      Object.entries(product.customizations).forEach(([key, customization]) => {
        initial[key] = customization.uiType === 'checkbox' ? [] : '';
      });
    }
    return initial;
  };

  const addProductToOrder = () => {
    const product = products.find((p) => p.id === selectedProductForAdd);
    if (!product) {
      actions.showSnackbar('Select a product first', 'error');
      return;
    }

    const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
    if (hasVariants && !tempVariant) {
      actions.showSnackbar('Variant selection is required', 'error');
      return;
    }

    const variant = hasVariants
      ? product.variants.find((v) => v.sku === tempVariant)
      : null;

    if (hasVariants && !isVariantSellable(variant)) {
      actions.showSnackbar('Selected variant is out of stock at this store', 'error');
      return;
    }

    const paidLines = stripFreeSkuLines(selectedProducts);
    const existingIndex = paidLines.findIndex((p) => p.id === product.id);

    const nextLine = {
      ...product,
      quantity: existingIndex >= 0 ? paidLines[existingIndex].quantity + 1 : 1,
      selectedVariant: variant,
      selectedCustomizations: { ...tempCustomizations }
    };

    const nextPaid =
      existingIndex >= 0
        ? paidLines.map((line, i) => (i === existingIndex ? nextLine : line))
        : [...paidLines, nextLine];

    setSelectedProducts(nextPaid);
    if (appliedCoupon) {
      setAppliedCoupon(null);
      setCouponCode('');
      actions.showSnackbar('Coupon cleared because the cart changed. Re-apply if needed.', 'info');
    } else {
      actions.showSnackbar('Product added', 'success');
    }

    setSelectedProductForAdd('');
    setTempVariant('');
    setTempCustomizations({});
  };

  const updateLineQuantity = (index, quantity) => {
    const line = selectedProducts[index];
    if (line?.isFreeSkuItem) return;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setSelectedProducts((prev) => {
      const updated = prev.map((item, i) => (i === index ? { ...item, quantity: qty } : item));
      return appliedCoupon ? stripFreeSkuLines(updated) : updated;
    });
    if (appliedCoupon) setAppliedCoupon(null);
  };

  const removeLine = (index) => {
    const line = selectedProducts[index];
    if (line?.isFreeSkuItem) {
      removeCoupon();
      return;
    }
    const next = selectedProducts.filter((_, i) => i !== index);
    setSelectedProducts(appliedCoupon ? stripFreeSkuLines(next) : next);
    if (appliedCoupon) setAppliedCoupon(null);
  };

  const revalidateStock = async () => {
    if (!assignedStore?.id) return { ok: false, message: 'Store not assigned' };

    const response = await fetch(
      `${appConfig.api.baseURL}/products?status=active&limit=100&storeId=${assignedStore.id}`,
      { headers: getAuthHeaders() }
    );
    const data = await response.json();
    if (!data.success) {
      return { ok: false, message: data.message || 'Failed to re-check stock' };
    }

    const freshProducts = data.data || [];
    setProducts(freshProducts);

    const updatedLines = selectedProducts.map((line) => {
      if (line.isFreeSkuItem) return line;
      const fresh = freshProducts.find((p) => p.id === line.id);
      const freshVariant = fresh?.variants?.find((v) => v.sku === line.selectedVariant?.sku);
      return {
        ...line,
        selectedVariant: freshVariant
          ? { ...line.selectedVariant, ...freshVariant }
          : { ...line.selectedVariant, isInStock: false }
      };
    });

    setSelectedProducts(updatedLines);

    const oos = updatedLines.filter((line) => !isLineSellable(line));
    if (oos.length > 0) {
      return {
        ok: false,
        message: `Out of stock: ${oos.map((l) => l.name).join(', ')}. Go back and update items.`
      };
    }

    return { ok: true, lines: updatedLines, catalog: freshProducts };
  };

  const placeOrder = async () => {
    if (!canContinueToSummary) {
      actions.showSnackbar('Please complete all required fields before placing order', 'error');
      return;
    }
    if (shippingCost == null || shippingError) {
      actions.showSnackbar('Wait for delivery fee before placing order', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const stockCheck = await revalidateStock();
      if (!stockCheck.ok) {
        actions.showSnackbar(stockCheck.message, 'error');
        setPage(1);
        return;
      }

      const lines = stockCheck.lines || selectedProducts;
      const cartProducts = lines.map(toCartProductLine);
      const cartSubtotal = cartProducts.reduce(
        (sum, line) => sum + (Number(line.price) || 0) * (Number(line.quantity) || 0),
        0
      );
      const discount = appliedCoupon?.couponCode
        ? Math.min(cartSubtotal, Math.max(0, Number(appliedCoupon.couponValue) || 0))
        : 0;
      const appliedCouponCode =
        discount > 0 && appliedCoupon?.couponCode
          ? String(appliedCoupon.couponCode).trim()
          : null;
      const couponDiscountType =
        appliedCoupon?.discountType === 'free_sku' ? 'free_sku' : 'discount_coupon';
      const shipAmount = Math.max(0, Number(shippingCost) || 0);
      const total = Math.max(0, cartSubtotal + shipAmount - discount);

      const lat = Number(selectedAddress?.latLong?.lat);
      const lng = Number(selectedAddress?.latLong?.lng);
      const deliveryAddress = {
        ...selectedAddress,
        latLong:
          Number.isFinite(lat) && Number.isFinite(lng)
            ? { lat, lng }
            : selectedAddress.latLong ?? null
      };

      const branchId = assignedStore?.branchId ?? assignedStore?.branch_id;
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const deliveryDateStr = formatLocalDate(deliveryDay === 'today' ? today : tomorrow);

      const orderData = {
        userId: String(customer.userId).trim(),
        addressId: selectedAddress.addressId != null ? String(selectedAddress.addressId) : undefined,
        deliveryAddress,
        storeId: assignedStore.id != null ? String(assignedStore.id) : undefined,
        storeDetails: assignedStore && typeof assignedStore === 'object' ? assignedStore : undefined,
        branch_id: branchId != null && branchId !== '' ? String(branchId) : undefined,
        timeSlotId: selectedTimeSlot?.id != null ? String(selectedTimeSlot.id) : null,
        timeSlotDetails: selectedTimeSlot && typeof selectedTimeSlot === 'object' ? selectedTimeSlot : undefined,
        pranaSlotId: null,
        deliveryDate: deliveryDateStr,
        deliveryDateTimestamp: Date.now(),
        products: cartProducts,
        subtotal: cartSubtotal,
        shippingCost: shipAmount,
        discount,
        discountType: appliedCouponCode ? couponDiscountType : null,
        appliedCoupon: appliedCouponCode,
        instructions: instructions.trim() || null,
        total,
        platform: 'WEB',
        deliveryType: 'Home Delivery',
        orderType: 'customer_order',
        createdBy: 'customer',
        status: 'pending'
      };

      const response = await fetch(`${appConfig.api.baseURL}/orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData)
      });
      const data = await response.json();

      if (data.success) {
        setCreatedOrderId(data.order?.orderId || '');
        setSuccessDialogOpen(true);
        actions.showSnackbar('COD order placed successfully', 'success');
      } else {
        actions.showSnackbar(data.message || 'Error placing order', 'error');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      actions.showSnackbar('Error placing order', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setPage(1);
    setCustomerSearch('');
    setCustomer(null);
    setCustomerError('');
    setAddresses([]);
    setSelectedAddressId('');
    resetFulfillmentDependentState();
    setDeliveryDay('today');
    setInstructions('');
    setCreatedOrderId('');
  };

  const productForAdd = products.find((p) => p.id === selectedProductForAdd);
  const sellableVariants =
    productForAdd?.variants?.filter(
      (v) => v.isActive !== false && v.visibleToCustomer !== false
    ) || [];

  const renderPage1 = () => (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">1. Customer</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="Customer Mobile Number"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && customerSearch.length === 10) searchCustomer();
              }}
              placeholder="Enter 10-digit mobile number"
              error={!!customerError}
              helperText={customerError}
              inputProps={{ maxLength: 10 }}
            />
            <Button
              variant="contained"
              startIcon={customerSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={searchCustomer}
              disabled={customerSearching || customerSearch.length !== 10}
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
          </Box>
          {customer && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Customer found: {customer.f_name || customer.name || 'N/A'} ({customer.userId})
            </Alert>
          )}
        </CardContent>
      </Card>

      {customer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">2. Delivery Address</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddAddressOpen(true)}
                sx={{ ml: 'auto' }}
              >
                Add New Address
              </Button>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Select Address</InputLabel>
              <Select
                value={selectedAddressId}
                label="Select Address"
                onChange={(e) => handleAddressSelection(e.target.value)}
              >
                {addresses.length === 0 && (
                  <MenuItem value="" disabled>
                    No addresses found — add a new one
                  </MenuItem>
                )}
                {addresses.map((address) => (
                  <MenuItem key={address.addressId} value={address.addressId}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {address.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {address.houseDetails}, {address.streetDetails}
                        {address.landmark ? `, ${address.landmark}` : ''} - {address.pincode}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(storeSelectionLoading || assignedStore) && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StoreIcon color="primary" />
                  <Typography variant="subtitle1">Assigned Store</Typography>
                  {storeSelectionLoading && <CircularProgress size={18} />}
                </Box>
                {assignedStore ? (
                  <>
                    <Typography variant="body1" fontWeight="bold">
                      {assignedStore.storeName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {assignedStore.address}
                    </Typography>
                    {typeof storeDistanceMeters === 'number' && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Distance: {(storeDistanceMeters / 1000).toFixed(2)} km
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Finding nearest store…
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {assignedStore && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CartIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">3. Products</Typography>
              {productsLoading && <CircularProgress size={18} sx={{ ml: 1 }} />}
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Stock shown for store: <strong>{assignedStore.storeName}</strong>. Out-of-stock variants cannot be added.
            </Alert>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Product</InputLabel>
              <Select
                value={selectedProductForAdd}
                label="Select Product"
                onChange={(e) => {
                  const productId = e.target.value;
                  setSelectedProductForAdd(productId);
                  const product = products.find((p) => p.id === productId);
                  setTempVariant('');
                  setTempCustomizations(initCustomizations(product));
                }}
                disabled={productsLoading}
              >
                <MenuItem value="">
                  <em>Choose a product</em>
                </MenuItem>
                {products.map((product) => {
                  const anyInStock = (product.variants || []).some(isVariantSellable);
                  return (
                    <MenuItem key={product.id} value={product.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>
                          {product.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={anyInStock ? 'In stock' : 'Out of stock'}
                          color={anyInStock ? 'success' : 'default'}
                        />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {productForAdd && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {productForAdd.name}
                </Typography>

                {sellableVariants.length > 0 && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Variant *</InputLabel>
                    <Select
                      value={tempVariant}
                      label="Variant *"
                      onChange={(e) => setTempVariant(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select a variant</em>
                      </MenuItem>
                      {sellableVariants.map((variant) => {
                        const inStock = variant.isInStock === true;
                        return (
                          <MenuItem key={variant.sku} value={variant.sku} disabled={!inStock}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography sx={{ flex: 1 }}>
                                {variant.weight} — ₹{variant.price}
                              </Typography>
                              <Chip
                                size="small"
                                label={inStock ? 'In stock' : 'Out of stock'}
                                color={inStock ? 'success' : 'error'}
                              />
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}

                {productForAdd.customizations &&
                  Object.entries(productForAdd.customizations).map(([key, customization]) => (
                    <Box key={key} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {customization.displayName || key}
                      </Typography>
                      {customization.uiType === 'checkbox' ? (
                        <FormGroup>
                          {(customization.options || []).map((option) => (
                            <FormControlLabel
                              key={option.id}
                              control={
                                <Checkbox
                                  checked={(tempCustomizations[key] || []).includes(option.id)}
                                  onChange={(e) => {
                                    const current = tempCustomizations[key] || [];
                                    setTempCustomizations({
                                      ...tempCustomizations,
                                      [key]: e.target.checked
                                        ? [...current, option.id]
                                        : current.filter((id) => id !== option.id)
                                    });
                                  }}
                                />
                              }
                              label={`${option.name}${
                                option.additionalPrice
                                  ? ` (+₹${option.additionalPrice})`
                                  : ''
                              }`}
                            />
                          ))}
                        </FormGroup>
                      ) : (
                        <RadioGroup
                          value={tempCustomizations[key] || ''}
                          onChange={(e) =>
                            setTempCustomizations({
                              ...tempCustomizations,
                              [key]: e.target.value
                            })
                          }
                        >
                          {(customization.options || []).map((option) => (
                            <FormControlLabel
                              key={option.id}
                              value={option.id}
                              control={<Radio />}
                              label={`${option.name}${
                                option.additionalPrice
                                  ? ` (+₹${option.additionalPrice})`
                                  : ''
                              }`}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    </Box>
                  ))}

                <Button variant="contained" startIcon={<AddIcon />} onClick={addProductToOrder}>
                  Add to Order
                </Button>
              </Box>
            )}

            {selectedProducts.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Variant</TableCell>
                      <TableCell>Stock</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedProducts.map((line, index) => {
                      const inStock = isLineSellable(line);
                      return (
                        <TableRow key={`${line.id}-${index}`}>
                          <TableCell>{line.name}{line.isFreeSkuItem ? ' (Free)' : ''}</TableCell>
                          <TableCell>{line.selectedVariant?.weight || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={line.isFreeSkuItem ? 'Free SKU' : inStock ? 'In stock' : 'Out of stock'}
                              color={line.isFreeSkuItem ? 'info' : inStock ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={line.quantity}
                              disabled={Boolean(line.isFreeSkuItem)}
                              onChange={(e) => updateLineQuantity(index, e.target.value)}
                              inputProps={{ min: 1, style: { width: 64 } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            ₹{(calculateLinePrice(line) * line.quantity).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <IconButton color="error" onClick={() => removeLine(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {assignedStore && selectedProducts.filter((l) => !l.isFreeSkuItem).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CouponIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">4. Coupon</Typography>
            </Box>
            {appliedCoupon ? (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" onClick={removeCoupon}>
                    Remove
                  </Button>
                }
              >
                {appliedCoupon.couponCode} applied — ₹{couponDiscount.toFixed(2)} off
                {appliedCoupon.discountType === 'free_sku' ? ' (free item)' : ''}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') applyCoupon();
                  }}
                />
                <Button
                  variant="contained"
                  onClick={applyCoupon}
                  disabled={couponApplying}
                  sx={{ minWidth: 120 }}
                >
                  {couponApplying ? 'Applying…' : 'Apply'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {assignedStore && selectedProducts.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">5. Delivery Slot</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant={deliveryDay === 'today' ? 'contained' : 'outlined'}
                onClick={() => setDeliveryDay('today')}
                sx={{ flex: 1, py: 1.5 }}
              >
                Today
              </Button>
              <Button
                variant={deliveryDay === 'tomorrow' ? 'contained' : 'outlined'}
                onClick={() => setDeliveryDay('tomorrow')}
                sx={{ flex: 1, py: 1.5 }}
              >
                Tomorrow
              </Button>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Choose Time Slot</InputLabel>
              <Select
                value={selectedTimeSlotId}
                label="Choose Time Slot"
                onChange={(e) => setSelectedTimeSlotId(e.target.value)}
                disabled={timeSlotsLoading || timeSlots.length === 0}
              >
                <MenuItem value="">
                  <em>{timeSlotsLoading ? 'Loading slots…' : 'Select a time slot'}</em>
                </MenuItem>
                {timeSlots.map((slot) => {
                  const availability = slot.availability || {};
                  const isAvailable =
                    availability.isAvailable !== false && !slot.isDisabled;
                  const label =
                    slot.displayTime ||
                    `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
                  return (
                    <MenuItem key={slot.id} value={slot.id} disabled={!isAvailable}>
                      {label}
                      {!isAvailable ? ' (Unavailable)' : ''}
                      {availability.maxLimit
                        ? ` — ${availability.currentCount || 0}/${availability.maxLimit}`
                        : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {assignedStore && selectedAddress && (
        <Alert
          severity={shippingError ? 'error' : shippingCost === 0 ? 'success' : 'info'}
          sx={{ mb: 3 }}
        >
          {shippingLoading && 'Calculating delivery fee…'}
          {!shippingLoading && shippingError && shippingError}
          {!shippingLoading && !shippingError && shippingCost != null && (
            <>
              Delivery fee: <strong>₹{Number(shippingCost).toFixed(2)}</strong>
              {subtotal >= 999 ? ' (free delivery above ₹999)' : ''}
              {typeof shippingDistanceKm === 'number' ? ` · ${Number(shippingDistanceKm).toFixed(1)} km` : ''}
            </>
          )}
          {!shippingLoading && !shippingError && shippingCost == null && 'Select address and products to quote delivery fee.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          disabled={!canContinueToSummary}
          onClick={() => setPage(2)}
        >
          Continue to Summary
        </Button>
      </Box>
    </>
  );

  const renderPage2 = () => (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Payment method: <strong>Cash on Delivery (COD)</strong>
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer
              </Typography>
              <Typography>
                {customer?.f_name || customer?.name || 'N/A'} ({customer?.userId})
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Store
              </Typography>
              <Typography fontWeight="bold">{assignedStore?.storeName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {assignedStore?.address}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Delivery Address
              </Typography>
              <Typography fontWeight="bold">{selectedAddress?.userName}</Typography>
              <Typography variant="body2">
                {selectedAddress?.houseDetails}, {selectedAddress?.streetDetails}
                {selectedAddress?.landmark ? `, ${selectedAddress.landmark}` : ''}
              </Typography>
              <Typography variant="body2">Pincode: {selectedAddress?.pincode}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Slot
              </Typography>
              <Typography>
                {deliveryDay === 'today' ? 'Today' : 'Tomorrow'} —{' '}
                {selectedTimeSlot?.displayTime ||
                  `${formatTime(selectedTimeSlot?.startTime)} - ${formatTime(
                    selectedTimeSlot?.endTime
                  )}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Items
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Variant</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProducts.map((line, index) => {
                  const inStock = isLineSellable(line);
                  return (
                    <TableRow key={`summary-${line.id}-${index}`}>
                      <TableCell>{line.name}{line.isFreeSkuItem ? ' (Free)' : ''}</TableCell>
                      <TableCell>{line.selectedVariant?.weight || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inStock ? 'In stock' : 'Out of stock'}
                          color={inStock ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{line.quantity}</TableCell>
                      <TableCell align="right">
                        ₹{(calculateLinePrice(line) * line.quantity).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography>Subtotal: ₹{subtotal.toFixed(2)}</Typography>
            <Typography>
              Shipping: {shippingLoading ? '…' : `₹${ship.toFixed(2)}`}
            </Typography>
            {couponDiscount > 0 && (
              <Typography color="success.main">
                Discount ({appliedCoupon?.couponCode}): −₹{couponDiscount.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">Total: ₹{orderTotal.toFixed(2)}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Delivery Instructions (optional)"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setPage(1)}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          onClick={placeOrder}
          disabled={isSubmitting || !canContinueToSummary}
        >
          Place COD Order
        </Button>
      </Box>
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CartIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Manual Order Creation
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Super Admin / Customer Care — place COD orders for customers who cannot use the mobile app.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip
            label="1. Build Order"
            color={page === 1 ? 'primary' : 'default'}
            variant={page === 1 ? 'filled' : 'outlined'}
          />
          <Chip
            label="2. Summary & Place"
            color={page === 2 ? 'primary' : 'default'}
            variant={page === 2 ? 'filled' : 'outlined'}
          />
        </Box>

        {page === 1 ? renderPage1() : renderPage2()}
      </Paper>

      <Dialog
        open={addAddressOpen}
        onClose={() => setAddAddressOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            height: { xs: 'auto', md: 620 },
            maxHeight: '90vh'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Address
          </Typography>
          <IconButton onClick={() => setAddAddressOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            height: { md: 'calc(620px - 64px)' },
            minHeight: 480
          }}
        >
          <Box sx={{ flex: { md: '1 1 58%' }, minHeight: 320, position: 'relative' }}>
            <Box sx={{ position: { md: 'absolute' }, inset: { md: 0 }, height: { xs: 320, md: '100%' } }}>
              <GoogleMapComponent
                overlaySearch
                showPinHint
                height="100%"
                selectedLocation={newAddress.latLong}
                onLocationSelect={(lat, lng) => {
                  setNewAddress((prev) => ({
                    ...prev,
                    latLong: { lat, lng }
                  }));
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: { md: '1 1 42%' },
              display: 'flex',
              flexDirection: 'column',
              px: 3,
              py: 2.5,
              borderLeft: { md: '1px solid' },
              borderColor: 'divider',
              minWidth: { md: 340 }
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
              <TextField
                fullWidth
                required
                variant="standard"
                label="Customer Name"
                value={newAddress.userName}
                onChange={(e) => setNewAddress({ ...newAddress, userName: e.target.value })}
                sx={{ mb: 2.5 }}
              />
              <TextField
                fullWidth
                required
                variant="standard"
                label="House Details"
                value={newAddress.houseDetails}
                onChange={(e) => setNewAddress({ ...newAddress, houseDetails: e.target.value })}
                sx={{ mb: 2.5 }}
              />
              <TextField
                fullWidth
                variant="standard"
                label="Street Details"
                value={newAddress.streetDetails}
                onChange={(e) => setNewAddress({ ...newAddress, streetDetails: e.target.value })}
                sx={{ mb: 2.5 }}
              />
              <TextField
                fullWidth
                variant="standard"
                label="Landmark"
                value={newAddress.landmark}
                onChange={(e) => setNewAddress({ ...newAddress, landmark: e.target.value })}
                sx={{ mb: 2.5 }}
              />
              <TextField
                fullWidth
                required
                variant="standard"
                label="Pincode"
                value={newAddress.pincode}
                onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2.5 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Latitude"
                  type="number"
                  value={newAddress.latLong.lat}
                  onChange={(e) =>
                    setNewAddress({
                      ...newAddress,
                      latLong: { ...newAddress.latLong, lat: parseFloat(e.target.value) || 0 }
                    })
                  }
                />
                <TextField
                  fullWidth
                  variant="standard"
                  label="Longitude"
                  type="number"
                  value={newAddress.latLong.lng}
                  onChange={(e) =>
                    setNewAddress({
                      ...newAddress,
                      latLong: { ...newAddress.latLong, lng: parseFloat(e.target.value) || 0 }
                    })
                  }
                />
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleAddAddress}
              disabled={
                !newAddress.userName.trim() ||
                !newAddress.houseDetails.trim() ||
                newAddress.pincode.trim().length !== 6
              }
              sx={{
                mt: 3,
                py: 1.4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 600
              }}
            >
              Save & Proceed
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={successDialogOpen} onClose={() => {}}>
        <DialogTitle>Order Placed</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            COD order created successfully.
          </Typography>
          {createdOrderId && (
            <Typography variant="h6" sx={{ mt: 1 }}>
              Order ID: {createdOrderId}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSuccessDialogOpen(false);
              resetAll();
            }}
          >
            Create Another
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessDialogOpen(false);
              if (createdOrderId) {
                navigate(`/super-admin/orders/view/${createdOrderId}`);
              } else {
                navigate('/super-admin/orders/list');
              }
            }}
          >
            View Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualOrderCreation;
